import { useQuery } from '@tanstack/react-query';
import { useLocale } from '../i18n/locale-context';
import { fetchCatalog } from '../lib/catalog-client';

/** The catalogue filters a just-saved search was opened on. */
export interface AlertWhenFilters {
  q?: string;
  region?: string;
  category?: string;
  ikpu?: string;
  nicheSlug?: string;
  scope?: string;
  /**
   * The list is a saved search's own — the `alert=` param, its whole definition
   * (`features.alertCriteriaLink`). Then the scalar filters above are all empty,
   * and the rate must be asked for THIS definition: without it the request was
   * the whole catalogue's, and a search with 34 open lots promised «≈ 3075 a la
   * semana» (persona test 22.10, D4).
   */
  alert?: string;
}

/**
 * «Te escribiremos a ana@empresa.es cuando salga una nueva (≈ 4 a la semana).»
 * — the second line of «Tu selección» (Spain, review 21.10 «first hour», item
 * 9). Tendios says when its email comes; a reader who knows one will arrive
 * does not go looking on PLACSP himself.
 *
 * The rate is the one the save popover shows (`SaveSearchButton`): how many
 * lots these filters matched over the last 30 days, per week — the same
 * request under the same key, so a reader who saved from the popover costs no
 * second one. On a saved search's own list (`alert=`) the filters are the
 * search's whole definition, so the number is that search's, never the
 * catalogue's. Under one a week, or while it loads, the line names no number.
 * Rendered only where alerts reach the address as it is
 * (`messaging.emailWithoutConfirmation`), so «te escribiremos» needs no
 * «confirma tu email» after it.
 */
export function AlertWhenNote({ email, filters }: { email: string; filters: AlertWhenFilters }) {
  const { t, locale } = useLocale();
  const { q, region, category, ikpu, nicheSlug, scope, alert } = filters;
  const { data } = useQuery({
    // `alert` is undefined for every list that is not a saved search's own, which
    // the key's hash drops — those keep the exact key the save popover uses.
    queryKey: ['saved-search-rate', { q, region, category, ikpu, nicheSlug, scope, alert }],
    staleTime: 5 * 60_000,
    queryFn: () =>
      fetchCatalog({
        q,
        region,
        category,
        ikpu,
        niche: nicheSlug,
        scope,
        alert,
        since: new Date(Date.now() - 30 * 86_400_000).toISOString(),
        limit: 1,
        lang: locale,
      }),
  });
  // 30 days ≈ 4.3 weeks — the popover's arithmetic.
  const perWeek = data ? Math.round(data.total / 4.3) : 0;
  return (
    <span data-testid="alert-when-note">
      {perWeek > 0
        ? t('catalog.alertWhenRate', { email, n: perWeek })
        : t('catalog.alertWhen', { email })}
    </span>
  );
}
